import { Res } from 'core/api';
import { Events, Log } from 'core/infrastructure';
import { Modal } from 'core/ui-next/modal';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import React, { ReactNode } from 'react';
import { DialogBody, DialogFooter, DialogHeader, SvgIcon } from 'core/ui-next/chakra-components';
import { addLineBreaks } from 'core/ui';
import { BasicButton } from 'core/ui-next';
import { ErrorWhite } from 'core/img';


const ErrorModal = ({ title, message, buttonText, close }: { title: string, message: string, buttonText: string; } & Modal.Actions<void>) => {
	return (
		<VStack w={'full'} gap={'4'} justifyItems={'center'} bg={{ base: 'white', _dark: '{colors.neutral.900}' }}>
			<VStack gap={'4'} w={'full'}>
				<DialogHeader paddingX={'4'} paddingY={'2.5'} h={'9'} bg={'error'} width={'full'}>
					<HStack gap={'3'} color={'white'}>
						<SvgIcon src={ErrorWhite} w={'4'} h={'4'} />
						<Text fontWeight={'semibold'} fontSize={'sm'}>{title || Res.Common.WARNING()}</Text>
					</HStack>
				</DialogHeader>
				<DialogBody p={'0'} paddingX={'4'} width={'full'} maxHeight={'60dvh'} overflowY={'auto'}>
					<Box fontSize={'sm'} textAlign={'center'} width={'full'}>
						<Text>
							{addLineBreaks(message)}
						</Text>
					</Box>
				</DialogBody>
			</VStack>
			<VStack w={'full'} paddingBottom={'4'} paddingX={'4'}>
				<DialogFooter width={'full'} p={'0'}>
					<BasicButton size='sm' text={buttonText} type={'error'} onClick={() => close()} />
				</DialogFooter>
			</VStack>
		</VStack>
	);
};


let onErrorMode: 'close' | 'reload' = 'reload';

function handleError(error: any, stack: any) {
	const errorMsg = `Ha ocurrido un error inesperado:
    > Navegador: ${navigator.userAgent}
    > Versión de Ágora: ${AGORA_VERSION}
    > Error: ${error}
    > StackTrace: ${stack || '<unknown>'}`;

	Log.error(errorMsg);
	void showErrorToastr(error);
}

async function showErrorToastr(error: any) {

	const buttonText = onErrorMode === 'close'
		? Res.WA.WA_UNEXPECTED_ERROR_CLOSE_BTN()
		: Res.WA.WA_UNEXPECTED_ERROR_RELOAD_BTN();

	const closeAction = onErrorMode === 'close'
		? getCloseFunctionForPlatform()
		: () => { window.location.assign('/'); };


	// Lo ejecutamos en un setTimeout para asegurarnos de que termine de ejecutarse previamente las tareas pendientes en el loop de javascript, añadimos esta macrotask
	// al final de la cola. Por ejemplo, puede haber microtasks pendientes como los then de las promesas y queremos que terminen de ejecutarse.
	setTimeout(async () => {
		await Modal.show(ErrorModal, { title: Res.WA.WA_UNEXPECTED_ERROR_TITLE(), message: error, buttonText, placement: 'top', size: 'sm', closeOnInteractOutside: false });
		closeAction();
	});
}

function getCloseFunctionForPlatform() {

	return () => {
		// En Cordova necesito invocar la opción de cierre nativo
		if (window.Cordova)
			return window.Cordova.exec(() => { }, () => { }, 'Platform', 'system.close');

		// En el caso de electron, un Window.close() es suficiente
		return window.close();
	};
}

type ErrorBoundaryProps = {
	children: ReactNode;
}

type ErrorBoundaryState = {
	hasError: boolean;
	error: Nullable<Error>;
}

export class ErrorBoundary extends React.Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
		Log.fatal(`Error inesperado:
> Navegador: ${navigator.userAgent}
> Versión de Ágora: ${AGORA_VERSION}
> Message: ${error.toString()}
> Stack: ${error.stack}
> ComponentStack: ${errorInfo.componentStack}
> Digest: ${errorInfo.digest}`);
	}

	render() {
		if (!this.state.hasError)
			return this.props.children;

		// Defino todos los estilos a mano porque no sé si tengo chakra o no, y tampoco
		// sé de qué tema (claro u oscuro) vengo.

		const containerStyle: React.CSSProperties = {
			position: 'fixed',
			inset: 0,
			display: 'flex',
			flexDirection: 'column',
			gap: '1rem',
			padding: '5rem',
			alignItems: 'center',
			justifyContent: 'center',
			background: 'white',
			'--scrollbar-thumb': 'var(--chakra-colors-neutral-400)',
			'--scrollbar-track': 'transparent',
			'--scrollbar-width': '0.5rem'
		};

		const titleStyle: React.CSSProperties = {
			fontSize: '2rem',
			color: 'var(--chakra-colors-red-800)',
			maxWidth: '20rem',
			marginBottom: '2rem'
		};

		const messageStyle: React.CSSProperties = {
			fontWeight: 800,
			color: 'var(--chakra-colors-neutral-800)',
			width: '100%',
			maxWidth: '20rem'
		};

		const detailsStyle: React.CSSProperties = {
			fontSize: '0.75rem',
			color: 'var(--chakra-colors-neutral-800)',
			width: '100%',
			maxWidth: '20rem',
		};

		const stackStyle: React.CSSProperties = {
			...detailsStyle,
			flexBasis: 0,
			flexGrow: 1,
			overflowY: 'auto'
		};

		const buttonStyle: React.CSSProperties = {
			color: 'white',
			background: 'var(--chakra-colors-blue-700)',
			borderRadius: '0.5rem',
			padding: '0.5rem 1rem',
			margin: '1rem 0',
			cursor: 'pointer'
		};

		const onClick = () => {
			if (onErrorMode === 'close') {
				getCloseFunctionForPlatform()();
			}
			else {
				window.location.reload();
			}
		};

		// No uso componentes de chakra porque puede estar desmontado.
		// Me la juego a usar variables de colores porque suelen estar
		// inyectadas y, en el peor de los casos, se verá todo en negro.

		return <div style={containerStyle}>
			<p style={titleStyle}>Ha ocurrido un error inesperado.</p>
			<p style={messageStyle}>{this.state.error?.toString()}</p>

			<button style={buttonStyle} onClick={onClick}>{onErrorMode === 'close' ? 'CERRAR APLICACIÓN' : 'RECARGAR'}</button>

			<p style={detailsStyle}>{navigator.userAgent}</p>
			<p style={detailsStyle}>{AGORA_VERSION}</p>
			<p style={stackStyle}>{this.state.error?.stack}</p>
		</div>;
	}
}


export function configure(options: {
	onErrorAction: 'close' | 'reload'
	onLicenseInvalid: (errorMessage: string) => Promise<void>,
}) {

	onErrorMode = options?.onErrorAction || 'reload';

	// Usamos una función de error básica hasta que carguemos todo lo que necesitamos
	window.onerror = (message: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) => {

		// eslint-disable-next-line no-console
		console.error(error || message);
		handleError(message, error?.stack || `${source || '<unknown>'}:${lineno || 0}:${colno || 0}`);
		return false;
	};


	window.addEventListener('unhandledrejection', (event: any) => {
		handleError(event.reason.message || event.reason || 'Uncaught promise', event.reason && event.reason.stack);
	});

	if (!('onunhandledrejection' in window)) {
		// Mega-ñapa para intentar mostrar algo en unhandled rejection.
		// Escucho el evento request:failed y si nadie hace un catch en un tiempo
		// prudencial, genero un error

		let timer: Nullable<NodeJS.Timeout> = null;

		Events.on('request:failed', async (errorMessage: string) => {
			timer = setTimeout(() => handleError(errorMessage, ''), 50);
		});

		const _then = Promise.prototype.then;

		Promise.prototype.then = function (this: any, onfullfilled?: any, onrejected?: any) {
			return _then.call(this, onfullfilled, function (this: any, ...args: any[]) {
				if (onrejected && timer) {
					clearTimeout(timer);
					timer = null;
				}
				if (onrejected) {
					return onrejected(...args);
				}
			});
		} as any;

		Promise.prototype.catch = function (this: any, onrejected?: any) {
			return Promise.prototype.then.call(this, null, onrejected);
		};
	}

	Events.on('license:invalid', async msg => {
		const errorMsg = `Ha ocurrido un error inesperado:
	> Navegador: ${navigator.userAgent}
	> Versión de Ágora: ${AGORA_VERSION}
	> Error: ${msg}
	> StackTrace: ''`;

		Log.error(errorMsg);
		await options.onLicenseInvalid(msg);
	});

	Log.enableConsole();

	// Patcheamos la consola para que lance errores cuando react genera un warning
	const consoleAsAny = window.console as any;

	type ComplexArg = { message: string, stack: string };

	function isComplexArg(arg: ComplexArg | string): arg is ComplexArg {
		return (arg as ComplexArg).message !== undefined && (arg as ComplexArg).stack !== undefined;
	}

	['warn', 'error'].forEach(name => {
		const method = consoleAsAny[name];
		consoleAsAny[name] = function (...args: any[]) {
			method.apply(window.console, args);

			if (args) {
				const errMsg: string = isComplexArg(args[0]) ? args[0].message : args[0];

				// Los warning de webpack los ignoro
				if (errMsg.match(/\[webpack-dev-server\] WARNING/))
					return;

				if (errMsg.match(/(^Warning: )|(react)/)) {
					handleError(errMsg, 'warn from console');
				}
			}
		};
	});
}
